/**
 * Storage Router - Feature Flag Strategy Pattern
 *
 * Routes storage operations to appropriate backend based on feature flags.
 * Implements Strategy pattern (GoF) for clean storage abstraction.
 *
 * Architecture:
 * - storage.js: localStorage implementation (sync)
 * - storageIndexedDB.js: IndexedDB implementation (async)
 * - storageRouter.js: Feature flag routing (this file)
 *
 * Usage:
 * - Collections layer imports from this file
 * - Feature flags control which backend is used
 * - No code changes needed for rollback (just toggle flags)
 *
 * Phases:
 * - Phase 1: FLAGS all false → localStorage only (current)
 * - Phase 2: PARALLEL_STORAGE true → write to both (testing)
 * - Phase 3: USE_INDEXEDDB true → IndexedDB primary (cutover)
 *
 * @module storageRouter
 */

import { FLAGS } from '../config/featureFlags';
import * as storageLS from './storage';
import * as storageIDB from './storageIndexedDB';
import { checkIndexedDBAvailability } from './indexedDBAvailability';
import { getOrgId } from '../utils/getOrgId';

/**
 * FT-365 v3 Layer B#1: parse an org- and (optionally) user-scoped storage key
 * and return { orgId, userId, isPlain, isVesselOnly }. Recognises 4 shapes:
 *   - module_{orgId}_{vesselId}  (canonical org-scoped 3-part)
 *   - module_{orgId}             (canonical org-scoped 2-part, e.g. fleet_crew_ORG)
 *   - module_{vesselId}          (LEGACY vessel-only 2-part; treated as UNSCOPED)
 *   - module                     (LEGACY plain top-level; treated as UNSCOPED)
 * plus a userId marker `_userId_{userId}` anywhere in the key for future user-scoped rows.
 *
 * Best-effort — never throws. If detection is ambiguous, returns { isPlain: true }
 * so the enforcer treats the row as UNSCOPED and rejects.
 */
function parseKeyScope(key) {
  const result = { orgId: null, userId: null, isPlain: false, isVesselOnly: false };
  if (typeof key !== 'string' || !key) {
    result.isPlain = true;
    return result;
  }

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  // Legacy vessel IDs like VSL001-25
  const LEGACY_VESSEL_RE = /^[A-Z]{2,4}[-\d]+[-\d]*$/i;

  // userId marker — canonical form is `_userId_{uuid}` anywhere in the key.
  const userIdIdx = key.indexOf('_userId_');
  if (userIdIdx !== -1) {
    const after = key.slice(userIdIdx + '_userId_'.length);
    const userSegment = after.split('_')[0];
    if (UUID_RE.test(userSegment)) {
      result.userId = userSegment;
    }
  }

  const parts = key.split('_');
  if (parts.length < 2) {
    // Plain top-level key — no org, no vessel. UNSCOPED.
    result.isPlain = true;
    return result;
  }

  const last = parts[parts.length - 1];
  const secondLast = parts.length >= 3 ? parts[parts.length - 2] : null;

  const lastIsUUID = UUID_RE.test(last);
  const lastIsLegacyVessel = LEGACY_VESSEL_RE.test(last);
  const secondLastIsUUID = secondLast ? UUID_RE.test(secondLast) : false;

  if (secondLastIsUUID && (lastIsUUID || lastIsLegacyVessel)) {
    // module_{orgId}_{vesselId} — canonical
    result.orgId = secondLast;
    return result;
  }

  if (lastIsUUID) {
    // module_{orgId} — canonical org-scoped 2-part (fleet_crew_ORG etc.)
    result.orgId = last;
    return result;
  }

  if (lastIsLegacyVessel) {
    // module_{vesselId} — LEGACY vessel-only (pre-org-scoping)
    result.isVesselOnly = true;
    return result;
  }

  // Neither shape matches — treat as plain.
  result.isPlain = true;
  return result;
}

/**
 * FT-368: true iff `key`, as resolved by toOrgScopedKey, genuinely carries an
 * orgId (canonical shapes `module_{orgId}` or `module_{orgId}_{vesselId}`).
 *
 * This is the correct predicate for "should the FT-365 org-scope read-guard be
 * enforced on this key?" — the guard can only meaningfully enforce keys that
 * embed an orgId. It deliberately does NOT key off the caller's `noScope` flag:
 * the org-scoped modules (fleet_crew, fleet_risk_assessments, hours_of_rest) are
 * declared `noScope:true` yet toOrgScopedKey rewrites them to `module_{orgId}`,
 * so they MUST stay enforced. Only truly-plain per-user identity keys such as
 * `current_user` (which stay plain after toOrgScopedKey) are exempted.
 *
 * Pure + best-effort: never throws; returns false on any parse failure.
 *
 * @param {string} key - the RESOLVED (post-toOrgScopedKey) storage key
 * @returns {boolean}
 */
export function isOrgScopedKey(key) {
  try {
    return Boolean(parseKeyScope(key).orgId);
  } catch (_) {
    return false;
  }
}

/**
 * Read JSON from storage (feature flag aware)
 *
 * @param {string} key - Storage key
 * @param {*} fallback - Fallback value if key not found
 * @param {Object} [options]
 * @param {boolean} [options.enforceOrgScope] FT-365 v3 Layer B#1: when true,
 *   parse the key, verify its orgId (and userId, if the key carries `_userId_`)
 *   matches the current session, and reject with `fallback` on any mismatch.
 *   Plain / vessel-only keys are also rejected. Dev-only warning logged.
 * @returns {Promise<*>} Stored value or fallback
 *
 * @example
 * const data = await readJSON('crew_VSL001', []);
 * const settings = await readJSON('vesselSettings_ORG_VSL', null, { enforceOrgScope: true });
 */
export const readJSON = async (key, fallback = null, options = {}) => {
  // FT-365 v3 Layer B#1: reject reads of stale rows whose composite key doesn't
  // match the current session BEFORE touching any storage backend. This is the
  // single choke point — every consumer that opts in gets the guard for free.
  if (options && options.enforceOrgScope) {
    try {
      const scope = parseKeyScope(key);
      if (scope.isPlain || scope.isVesselOnly) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[FT-365 read-guard] Rejected non-org-scoped read: key=${key}`);
        }
        return fallback;
      }
      const currentOrgId = (() => {
        try { return getOrgId(); } catch (_) { return null; }
      })();
      if (scope.orgId && currentOrgId && scope.orgId !== currentOrgId) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[FT-365 read-guard] Rejected cross-org read: key=${key} keyOrgId=${scope.orgId} currentOrgId=${currentOrgId}`);
        }
        return fallback;
      }
      if (scope.userId) {
        // Best-effort userId check — the current-user id lives in localStorage
        // under `current_user`. If it can't be read, we conservatively fall
        // through rather than reject (matches getOrgId's fallback behaviour).
        let currentUserId = null;
        try {
          // eslint-disable-next-line no-restricted-syntax -- FT-365 v3 Layer B#1 read guard boundary read
          const cu = JSON.parse(localStorage.getItem('current_user') || '{}');
          currentUserId = cu && cu.id ? cu.id : null;
        } catch (_) { /* non-fatal */ }
        if (currentUserId && scope.userId !== currentUserId) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn(`[FT-365 read-guard] Rejected cross-user read: key=${key} keyUserId=${scope.userId} currentUserId=${currentUserId}`);
          }
          return fallback;
        }
      }
    } catch (guardErr) {
      // Guard failure must NOT break reads — log dev-only and fall through.
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[FT-365 read-guard] enforcement failed (falling through):', guardErr);
      }
    }
  }

  // IndexedDB mode: read from IndexedDB, fallback to localStorage
  if (FLAGS.USE_INDEXEDDB) {
    // Check IndexedDB availability before attempting to use it
    const availability = await checkIndexedDBAvailability();

    if (!availability.available) {
      console.warn('[StorageRouter] IndexedDB not available, using localStorage:', availability.reason);
      return storageLS.readJSON(key, fallback);
    }

    try {
      const value = await storageIDB.readJSON(key, fallback);

      // If not in IndexedDB, try localStorage (migration safety)
      if (value === null || value === undefined) {
        return storageLS.readJSON(key, fallback);
      }

      return value;
    } catch (error) {
      console.warn('[StorageRouter] IndexedDB read failed, using localStorage:', error);
      return storageLS.readJSON(key, fallback);
    }
  }

  // Default mode: localStorage only (current behavior)
  return storageLS.readJSON(key, fallback);
};

/**
 * Write JSON to storage (feature flag aware)
 *
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 * @param {{ source?: string }} [options] - FT-457: `source:'cloud-hydrate'`
 *        tags a cloud download/hydration echo. It rides onto the resulting
 *        `sr:storage:set` event detail so the UniversalSyncBridge FT-68
 *        justPulled cooldown drops ONLY that echo and NEVER a genuine user
 *        write (which leaves `source` undefined). Backward-compatible: every
 *        existing 2-arg caller is a genuine write and stays untagged.
 * @returns {Promise<boolean>} Success status
 *
 * @example
 * await writeJSON('crew_VSL001', crewData);
 * await writeJSON('defects_VSL001', merged, { source: 'cloud-hydrate' });
 */
export const writeJSON = async (key, value, options = {}) => {
  const source = options && options.source;
  // WEEK 6-7: Parallel mode now handles BOTH localStorage and IndexedDB writes internally
  // This eliminates redundant writes in caller code and centralizes event emission
  if (FLAGS.PARALLEL_STORAGE) {
    // Write to localStorage FIRST (synchronous) with event emission
    const lsSuccess = storageLS.writeJSON(key, value, source);

    if (!lsSuccess) {
      console.error('[StorageRouter] localStorage write failed in parallel mode');
      return false;
    }

    // Then write to IndexedDB (async, fire-and-forget, no event)
    const availability = await checkIndexedDBAvailability();
    if (!availability.available) {
      console.warn('[StorageRouter] IndexedDB not available:', availability.reason);
      return true; // localStorage succeeded, so operation is successful
    }

    try {
      await storageIDB.writeJSON(key, value, true); // silent=true (no event duplication)
      return true;
    } catch (error) {
      console.warn('[StorageRouter] Parallel IndexedDB write failed:', key, error);
      return true; // localStorage succeeded, so operation is successful
    }
  }

  // IndexedDB mode: write to IndexedDB only
  // FT-457: this is the LIVE prod path (USE_INDEXEDDB:true, PARALLEL_STORAGE:false).
  // The `source` tag MUST reach storageIDB.writeJSON here (silent=false so the
  // echo event fires with the tag). Tagging only the LS path would do nothing
  // in prod — that path is dead when PARALLEL_STORAGE is false.
  if (FLAGS.USE_INDEXEDDB) {
    // Check IndexedDB availability before attempting to use it
    const availability = await checkIndexedDBAvailability();

    if (!availability.available) {
      console.error('[StorageRouter] IndexedDB not available, cannot write:', availability.reason);
      // Fallback to localStorage in IndexedDB mode if IndexedDB unavailable
      return storageLS.writeJSON(key, value, source);
    }

    return await storageIDB.writeJSON(key, value, false, source);
  }

  // Default mode: localStorage only (current behavior)
  return storageLS.writeJSON(key, value, source);
};

/**
 * Remove item from storage (feature flag aware)
 *
 * @param {string} key - Storage key to remove
 * @returns {Promise<boolean>} Success status
 */
export const removeJSON = async (key) => {
  // Parallel mode: remove from BOTH
  if (FLAGS.PARALLEL_STORAGE) {
    storageLS.removeItem(key);
    await storageIDB.removeItem(key);
    return true;
  }

  // IndexedDB mode: remove from IndexedDB only
  if (FLAGS.USE_INDEXEDDB) {
    return await storageIDB.removeItem(key);
  }

  // Default mode: localStorage only
  return storageLS.removeItem(key);
};

/**
 * Clear all storage (feature flag aware)
 * USE WITH CAUTION
 *
 * @returns {Promise<boolean>} Success status
 */
export const clearAll = async () => {
  // Always clear both to be safe
  storageLS.clearAll();

  if (FLAGS.USE_INDEXEDDB || FLAGS.PARALLEL_STORAGE) {
    await storageIDB.clearAll();
  }

  return true;
};

/**
 * Get all keys matching pattern (feature flag aware)
 *
 * @param {string} pattern - Optional pattern to filter keys
 * @returns {Promise<Array<string>>} Array of matching keys
 *
 * @example
 * const vesselKeys = await getKeys('VSL001');
 */
export const getKeys = async (pattern) => {
  // IndexedDB mode: get keys from IndexedDB
  if (FLAGS.USE_INDEXEDDB) {
    try {
      return await storageIDB.getKeys(pattern);
    } catch (error) {
      console.warn('[StorageRouter] IndexedDB getKeys failed, using localStorage:', error);
      return storageLS.getKeys(pattern);
    }
  }

  // Default mode: localStorage only
  return storageLS.getKeys(pattern);
};

/**
 * Get storage statistics
 *
 * @returns {Promise<Object>} Storage usage info
 */
export const getStorageInfo = async () => {
  const lsInfo = storageLS.getStorageInfo ? storageLS.getStorageInfo() : { backend: 'localStorage' };

  if (FLAGS.USE_INDEXEDDB || FLAGS.PARALLEL_STORAGE) {
    const idbInfo = await storageIDB.getStorageInfo ? await storageIDB.getStorageInfo() : {};
    return {
      ...lsInfo,
      indexedDB: idbInfo,
      mode: FLAGS.PARALLEL_STORAGE ? 'parallel' : 'indexedDB'
    };
  }

  return lsInfo;
};

// Note: extractModule and extractVesselId are internal to storage.js and storageRouter.js
// If needed by external code, they should be exported separately from this file
